/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  FormRootSchema,
  FormState,
  FormStateSchema,
  whenAuRegion,
  whenCreateOrEditMode,
  whenNzRegion,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/builder-shared';
import { PRIVILEGED_EP_SYS_VARS_FRANCHISEE, SystemRoleBit } from '@gh/builder-shared-data';
import {
  collectVisibleValues,
  DateTimeTypes,
  MaskedTypes,
  NumberTypes,
  TextTypes,
  toAppFormGroup
} from '@gh/common-shared';

import { FormMode, getControlValue, patchControlValue, setControlValue } from '@gh/core-ui';

import { SystemVarsFranchisee, SystemVarsLocation } from '../../resources';

@Injectable()
export class SystemVarsFranchiseeFormState extends FormState<SystemVarsFranchisee> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext) {
    super(schemaFactory);
  }

  get submitValue(): SystemVarsFranchisee {
    return {
      ...collectVisibleValues(toAppFormGroup(this.group)),
      usersFromOtherOfficesAccess: getControlValue(this.group, 'usersFromOtherOfficesAccess'),
      usersEmailAccessFromOtherOffices: getControlValue(this.group, 'usersEmailAccessFromOtherOffices'),
    };
  }

  setLocation(location: SystemVarsLocation): void {
    patchControlValue(this.group, 'address', {
      city: location ? location.city : void 0,
      state: location ? location.state : void 0,
      stateCode: location ? location.state : void 0,
      postCode: location ? location.postcode : void 0,
    });
  }

  updateLicenseState(): void {
    this.get('licenseNo').updateSchema({
      enabled: getControlValue(this.group, 'appliesBuilderLicenceNo'),
    });
  }

  onRecalculateAssignmentFee(): void {
    const onSellDate = this.get('billing.onSellDate').value;
    const contractSignedDate = this.get('billing.contractSignedDate').value;
    const saleAmount = this.get('billing.saleAmount').value;

    const within18Months = this.get('billing.within18Months').value;
    const within24Months = this.get('billing.within24Months').value;
    const within36Months = this.get('billing.within36Months').value;
    const after36Months = this.get('billing.after36Months').value;

    const monthsBtwnDates = (startDate: Date, endDate: Date) => {
      if (!startDate || !endDate) {
        return null;
      }
      startDate = new Date(startDate);
      endDate = new Date(endDate);
      return Math.abs(
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        endDate.getMonth() -
        startDate.getMonth());
    };

    const months = monthsBtwnDates(onSellDate, contractSignedDate);

    if (!months) {
      setControlValue(this.group, 'billing.assignmentFee', null);
    } else {
      if (months <= 18) {
        setControlValue(this.group, 'billing.assignmentFee', saleAmount * within18Months / 100);
      } else {
        if (months <= 24) {
          setControlValue(this.group, 'billing.assignmentFee', saleAmount * within24Months / 100);
        } else {
          if (months <= 36) {
            setControlValue(this.group, 'billing.assignmentFee', saleAmount * within36Months / 100);
          } else {
            if (months > 36) {
              setControlValue(this.group, 'billing.assignmentFee', saleAmount * after36Months / 100);
            }
          }
        }
      }
    }
  }

  onRecalculateRenewalDate(): void {
    const term = this.get('billing.term').value;
    const currentAgreement = this.get('billing.currentAgreement').value;
    const originalCommencement = this.get('billing.originalCommencement').value;
    if (!currentAgreement) {
      setControlValue(this.group, 'billing.renewalDate',
                      new Date(originalCommencement.getFullYear() + term, originalCommencement.getMonth(), originalCommencement.getDate()));
    } else {
      setControlValue(this.group, 'billing.renewalDate',
                      new Date(currentAgreement.getFullYear() + term, currentAgreement.getMonth(), currentAgreement.getDate()));
    }
  }

  protected buildSchema(franchisee: SystemVarsFranchisee, { root, group }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasSystemRole(SystemRoleBit.Corporate)
    || this.userContext.hasSystemRole(SystemRoleBit.Master)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    // The whole form that has no "always inactive" active for Master/Corporate
    return root({
      enabled: whenCreateOrEditMode(),
      mode,
      privilegedEntryPoints: [PRIVILEGED_EP_SYS_VARS_FRANCHISEE],
      optimisticLock: true,
      children: {
        id: void 0,
        email: { enabled: false },
        typeOfBusiness: void 0,
        formedDate: { type: DateTimeTypes.ShortDate },
        companyName: { required: true, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        displayName: { required: true, type: TextTypes.OfficeName, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        systemName: { enabled: false },
        taxId: { visible: whenUsRegion(), type: MaskedTypes.TaxId, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        tradingAs: { required: true, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        abn: { visible: whenAuRegion(), type: MaskedTypes.Abn, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        acn: { visible: whenAuRegion(), type: MaskedTypes.Acn, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        gst: { visible: whenNzRegion(), type: MaskedTypes.Gst, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        phoneOffice: void 0,
        fax: void 0,
        phoneMobile: void 0,
        street1: { required: true, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        street2: { privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        city: { required: true, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        state: { visible: whenUsOrAuRegion(), required: true, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        postCode: { required: true, type: MaskedTypes.Zip, privileged: PRIVILEGED_EP_SYS_VARS_FRANCHISEE },
        licenseNo: {
          enabled: franchisee.appliesBuilderLicenceNo,
        },
        publishToWeb: {
          enabled: franchisee.isPublishToWebAvailable,
        },
        appliesBuilderLicenceNo: void 0,
        usersFromOtherOfficesAccess: {
          visible: this.isUsersFromOtherOfficesAccessAllow(),
          enabled: franchisee.isUsersFromOtherOfficesAccessActive,
        },
        isUsersFromOtherOfficesAccessActive: void 0,
        usersEmailAccessFromOtherOffices: {
          visible: this.isUsersFromOtherOfficesAccessAllow(),
          enabled: franchisee.isUsersEmailAccessFromOtherOfficesActive !== false,
        },
        isUsersEmailAccessFromOtherOfficesActive: void 0,
        billing: group({
          children: {
            contractSignedDate: { required: true, type: DateTimeTypes.ShortDate },
            depositDate: { required: true, type: DateTimeTypes.ShortDate },
            originalCommencement: { required: true, type: DateTimeTypes.ShortDate },
            currentAgreement: { required: true, type: DateTimeTypes.ShortDate },
            renewalDate: { required: true, type: DateTimeTypes.ShortDate },
            officeClosed: { type: DateTimeTypes.ShortDate },
            term: { required: true, type: NumberTypes.Quantity },
            taxRate: { required: true, type: NumberTypes.Percents },
            initialFranchiseFee: { type: NumberTypes.SignedPrice },
            franchiseFee: { required: true, type: NumberTypes.Percents },
            variationFee: { type: NumberTypes.Percents },
            within18Months: { type: NumberTypes.Percents },
            within24Months: { type: NumberTypes.Percents },
            within36Months: { type: NumberTypes.Percents },
            after36Months: { type: NumberTypes.Percents },
            onSellDate: { type: DateTimeTypes.ShortDate },
            saleAmount: { type: NumberTypes.SignedPrice },
            assignmentFee: { type: NumberTypes.SignedPrice },
          },
        }),
      },
    });
  }

  private isUsersFromOtherOfficesAccessAllow(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master);
  }
}
